import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Divider,
  Button,
  FormControl,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import DownloadIcon from '@mui/icons-material/Download';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import api from '../api';
import { toast } from 'react-toastify';
import BillingFilterBar, { matchesSearch, normalisePaymentStatus } from './BillingFilterBar';
import BillingPagination, { useBillingPagination } from './BillingPagination';

/** Backend may return /storage/... paths; parent SPA needs absolute URL to the API host. */
function resolveReceiptOpenUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const t = url.trim();
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/')) {
    const base = String(api.defaults?.baseURL || '')
      .replace(/\/api\/?$/, '')
      .replace(/\/$/, '');
    if (base) return `${base}${t}`;
  }
  return t;
}

/** Stripe-hosted PDFs are cross-origin; programmatic <a download> usually does not save the file. Open in a new tab instead. */
function openReceiptInBrowser(url) {
  const resolved = resolveReceiptOpenUrl(url);
  if (!resolved) return;
  window.open(resolved, '_blank', 'noopener,noreferrer');
}

const SubscriptionList = ({
  subscriptions,
  loading,
  parentStudents = [],
  onAssignCourseToStudent,
  assigningPlanKey = null,
  onPayInstallment = null,
  payingInstallmentId = null,
  showPayColumn = true,
  /** When set (admin viewing a parent’s billing), registration receipts use the admin API instead of parent routes. */
  billingParentId = null,
  /** Show the search + "Filter by Status" bar (parent Billing page). Off for the admin view. */
  showFilters = false,
}) => {
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const {
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    paginate,
  } = useBillingPagination(`${search}|${statusFilter}`);
  const [downloadingRegistrationPaymentId, setDownloadingRegistrationPaymentId] = useState(null);
  const [loadingCashierSubscriptionReceiptId, setLoadingCashierSubscriptionReceiptId] = useState(null);
  /** Same course+price can appear twice when two children are enrolled — include learner id. */
  const planKey = (sub) => {
    const pid = sub.course_price_id != null ? sub.course_price_id : 'n';
    const sid = sub.assigned_student_id ?? sub.student_id ?? 'u';
    return `${sub.course_id}_${pid}_${sid}`;
  };

  const parseDateToTimestamp = (value) => {
    if (!value) return 0;

    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    const nativeParsed = Date.parse(value);
    if (!Number.isNaN(nativeParsed)) return nativeParsed;

    // Handle "dd-MMM-yyyy" values coming from API (e.g. 27-Mar-2026)
    const dmyMatch = String(value).match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (!dmyMatch) return 0;

    const [, dayRaw, monthRaw, yearRaw] = dmyMatch;
    const monthMap = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };
    const monthIndex = monthMap[monthRaw.toLowerCase()];
    if (monthIndex == null) return 0;

    return new Date(Number(yearRaw), monthIndex, Number(dayRaw)).getTime();
  };

  const getLatestSubscriptionTimestamp = (sub) => {
    const directDates = [
      sub.purchased_at,
      sub.paid_at,
      sub.created_at,
      sub.updated_at,
      sub.subscription_created_at,
    ];

    const paymentDates = Array.isArray(sub.payments)
      ? sub.payments.flatMap((payment) => [payment?.date_paid, payment?.due_date])
      : [];

    const timestamps = [...directDates, ...paymentDates]
      .map(parseDateToTimestamp)
      .filter((time) => time > 0);

    return timestamps.length > 0 ? Math.max(...timestamps) : 0;
  };
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Loading subscriptions...</Typography>
      </Box>
    );
  }

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <Box sx={{ 
        p: 6, 
        textAlign: 'center', 
        bgcolor: '#f8f9fa', 
        borderRadius: 4,
        border: '1px dashed rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.03)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.5 }} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          No subscriptions found
        </Typography>
      </Box>
    );
  }

  // Flatten the subscriptions array (guard missing/malformed groups from API)
  const flatSubscriptions = subscriptions.flatMap((group) => {
    const subs = Array.isArray(group?.subscriptions) ? group.subscriptions : [];
    return subs.map((sub) => ({
      ...sub,
      payments: Array.isArray(sub?.payments) ? sub.payments : [],
      student_name: group.student_name,
      student_id: group.student_id
    }));
  });

  const sortedSubscriptions = [...flatSubscriptions].sort((a, b) => {
    const timeDiff = getLatestSubscriptionTimestamp(b) - getLatestSubscriptionTimestamp(a);
    if (timeDiff !== 0) return timeDiff;
    return (b.course_id || 0) - (a.course_id || 0);
  });

  // A course matches the status filter when any of its payments has that status (e.g.
  // "Pending" finds courses with an installment still to pay).
  const visibleSubscriptions = sortedSubscriptions.filter(
    (sub) =>
      (!statusFilter ||
        sub.payments.some((pay) => normalisePaymentStatus(pay?.status) === statusFilter)) &&
      matchesSearch(search, [
        sub.course_name,
        sub.plan_name,
        sub.location,
        sub.academic_year,
        sub.student_name,
        sub.assigned_student_name,
      ])
  );
  const { page, count: subscriptionCount, rows: pagedSubscriptions } = paginate(visibleSubscriptions);

  if (flatSubscriptions.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <Typography variant="h6" color="text.secondary">
          No subscriptions found
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return 'success';
      case 'PAY':
      case 'PENDING':
        return 'warning';
      case 'UNPAID':
      case 'FAILED':
        return 'error';
      case 'NOT_DUE':
        return 'default';
      default:
        return 'default';
    }
  };

  /** Readable status for chips (raw API values kept for logic). */
  const getStatusLabel = (status) => {
    const u = status?.toUpperCase();
    if (u === 'PAID') return 'Paid';
    if (u === 'PENDING') return 'Due';
    if (u === 'PAY') return 'Pay';
    if (u === 'FAILED' || u === 'VOID') return status || '—';
    if (u === 'NOT_DUE') return 'Not due';
    return status || '—';
  };

  const formatMoney = (value, currency) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(n)) return String(value);
    const raw = (currency || 'GBP').toString().trim();
    const c =
      raw === '£' || raw.toLowerCase() === 'gbp'
        ? 'GBP'
        : raw === '€' || raw.toLowerCase() === 'eur'
          ? 'EUR'
          : raw.toUpperCase();
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c }).format(n);
    } catch {
      return `${n.toFixed(2)} ${c}`;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {showFilters && (
        <BillingFilterBar
          search={search}
          onSearchChange={setSearch}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          placeholder="Search by course, plan, student..."
          statusOptions={[
            { value: 'paid', label: 'Paid' },
            { value: 'pending', label: 'Pending' },
          ]}
        />
      )}
      {showFilters && visibleSubscriptions.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="body1" fontWeight={600}>
            No courses match your search or status filter.
          </Typography>
        </Box>
      )}
      {pagedSubscriptions.map((sub, index) => (
        <Card 
          key={`${planKey(sub)}_${index}`} 
          elevation={0}
          sx={{ 
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              {/* Course Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '8px', 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea'
                }}>
                  <SchoolIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#2d3748">
                    {sub.course_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {sub.plan_name || 'Course Subscription'}
                  </Typography>
                  {sub.location && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Location: {sub.location}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, height: 30, alignSelf: 'center' }} />

              {/* Student assign: only show dropdown when parent has more than one student; otherwise show single student name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}>
                <PersonIcon fontSize="small" color="action" />
                {onAssignCourseToStudent && parentStudents && parentStudents.length > 1 ? (
                  <FormControl size="small" sx={{ minWidth: 160 }} disabled={assigningPlanKey === planKey(sub)}>
                    <Select
                      value={sub.assigned_student_id ?? sub.student_id ?? ''}
                      onChange={(e) => onAssignCourseToStudent(sub.course_id, e.target.value, sub.course_price_id)}
                      displayEmpty
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    >
                      <MenuItem value="">
                        <em>Not Assigned</em>
                      </MenuItem>
                      {parentStudents.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.full_name || s.first_name + ' ' + (s.last_name || '')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#4a5568">
                      {parentStudents && parentStudents.length === 1
                        ? (parentStudents[0].full_name || parentStudents[0].first_name + ' ' + (parentStudents[0].last_name || ''))
                        : (sub.assigned_student_name === 'Unassigned' || !sub.assigned_student_name ? 'Not Assigned' : sub.assigned_student_name)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Assigned student
                    </Typography>
                  </Box>
                )}
                {assigningPlanKey === planKey(sub) && (
                  <CircularProgress size={18} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
                )}
              </Box>
            </Box>

            <Chip 
              label={sub.academic_year} 
              size="small" 
              sx={{ 
                fontWeight: 600, 
                borderRadius: '6px',
                bgcolor: 'rgba(0,0,0,0.05)',
                color: '#4a5568'
              }}
            />
          </Box>

          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#718096', py: 2, pl: 3 }}>Payment type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#718096' }}>Due date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#718096' }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#718096' }}>Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#718096' }}>Balance</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#718096' }}>Status</TableCell>
                    {showPayColumn && (
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#718096' }}>Pay</TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#718096', pr: 3 }}>Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sub.payments.map((payment, payIndex) => {
                    const cur = payment.currency || 'GBP';
                    // Only the first unpaid installment in list order may be paid next (matches backend checkout rules).
                    const firstDueInstallmentIdx = sub.payments.findIndex(
                      (p) =>
                        p.status?.toUpperCase() === 'PENDING' &&
                        p.parent_subscription_installment_id
                    );
                    const showPayNow =
                      onPayInstallment &&
                      payment.status?.toUpperCase() === 'PENDING' &&
                      Boolean(payment.parent_subscription_installment_id) &&
                      payIndex === firstDueInstallmentIdx &&
                      firstDueInstallmentIdx !== -1;
                    return (
                    <TableRow
                      key={payIndex}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' },
                        ...(showPayNow
                          ? { bgcolor: 'rgba(37, 99, 235, 0.06)', boxShadow: 'inset 3px 0 0 #2563eb' }
                          : {}),
                      }}
                    >
                      <TableCell sx={{ py: 2, pl: 3 }}>
                        <Typography variant="body2" fontWeight={600} color="text.primary" component="span" display="block">
                          {payment.payment_type || payment.term_name}
                        </Typography>
                        {payment.term_name &&
                          payment.payment_type &&
                          payment.term_name !== payment.payment_type && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              {payment.term_name}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4a5568' }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                          <Typography variant="body2">{payment.due_date}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatMoney(payment.amount_due, cur)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {formatMoney(payment.paid_amount, cur)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: parseFloat(payment.balance) > 0 ? 'error.main' : 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        {formatMoney(payment.balance, cur)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(payment.status)}
                          color={getStatusColor(payment.status)}
                          size="small"
                          variant={payment.status === 'NOT_DUE' ? 'outlined' : 'filled'}
                          sx={{ minWidth: 88, fontWeight: 700, borderRadius: '6px' }}
                        />
                      </TableCell>
                      {showPayColumn && (
                        <TableCell align="center">
                          {showPayNow ? (
                            <Tooltip title="Pay this installment now" arrow>
                              <Button
                                size="small"
                                startIcon={payingInstallmentId === payment.parent_subscription_installment_id ? <CircularProgress size={14} color="inherit" /> : <PaymentIcon />}
                                variant="contained"
                                disabled={payingInstallmentId === payment.parent_subscription_installment_id}
                                sx={{
                                  bgcolor: '#2563eb',
                                  color: '#fff',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
                                  '&:hover': {
                                    bgcolor: '#1d4ed8',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                                  }
                                }}
                                onClick={() => onPayInstallment(payment.parent_subscription_installment_id)}
                              >
                                {payingInstallmentId === payment.parent_subscription_installment_id ? 'Redirecting…' : 'Pay Now'}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ pr: 3 }}>
                        {payment.status?.toUpperCase() === 'PAID' ? (
                          // Payment is PAID — Stripe invoice PDF/hosted URL, or server-resolved registration receipt, or installment receipt.
                          (payment.receipt_pdf_url || payment.receipt_url) ? (
                            <Tooltip title="Open receipt in a new tab (use Save as or Print to PDF to download)" arrow>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                variant="outlined"
                                sx={{
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                  bgcolor: 'rgba(22, 163, 74, 0.05)',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    bgcolor: 'rgba(22, 163, 74, 0.1)',
                                    borderColor: '#16a34a'
                                  }
                                }}
                                onClick={() =>
                                  openReceiptInBrowser(payment.receipt_pdf_url || payment.receipt_url)
                                }
                              >
                                Receipt
                              </Button>
                            </Tooltip>
                          ) : payment.course_registration_payment_id ? (
                            <Tooltip title="View or download receipt" arrow>
                              <Button
                                size="small"
                                startIcon={
                                  downloadingRegistrationPaymentId === payment.course_registration_payment_id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <DownloadIcon />
                                  )
                                }
                                variant="outlined"
                                disabled={downloadingRegistrationPaymentId === payment.course_registration_payment_id}
                                sx={{
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                  bgcolor: 'rgba(22, 163, 74, 0.05)',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    bgcolor: 'rgba(22, 163, 74, 0.1)',
                                    borderColor: '#16a34a',
                                  },
                                }}
                                onClick={async () => {
                                  const id = payment.course_registration_payment_id;
                                  setDownloadingRegistrationPaymentId(id);
                                  try {
                                    const receiptPath =
                                      billingParentId != null
                                        ? `admin/parent/${billingParentId}/course-registration-receipt`
                                        : 'parent/course/registration/receipt';
                                    const res = await api.get(receiptPath, {
                                      params: { course_registration_payment_id: id },
                                    });
                                    if (res.data?.success && res.data?.receipt_url) {
                                      window.open(resolveReceiptOpenUrl(res.data.receipt_url), '_blank');
                                    } else {
                                      toast.error(res.data?.message || 'Receipt not available.');
                                    }
                                  } catch (err) {
                                    toast.error(err.response?.data?.message || 'Failed to load receipt.');
                                  } finally {
                                    setDownloadingRegistrationPaymentId(null);
                                  }
                                }}
                              >
                                {downloadingRegistrationPaymentId === payment.course_registration_payment_id ? '…' : 'Receipt'}
                              </Button>
                            </Tooltip>
                          ) : payment.parent_subscription_installment_id ? (
                            <Tooltip title="Download receipt" arrow>
                              <Button
                                size="small"
                                startIcon={downloadingReceiptId === payment.parent_subscription_installment_id ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                                variant="outlined"
                                disabled={downloadingReceiptId === payment.parent_subscription_installment_id}
                                sx={{
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                  bgcolor: 'rgba(22, 163, 74, 0.05)',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    bgcolor: 'rgba(22, 163, 74, 0.1)',
                                    borderColor: '#16a34a'
                                  }
                                }}
                                onClick={async () => {
                                  setDownloadingReceiptId(payment.parent_subscription_installment_id);
                                  try {
                                    const res = await api.get('parent/course/installment/receipt', {
                                      params: { parent_subscription_installment_id: payment.parent_subscription_installment_id },
                                    });
                                    if (res.data?.success && res.data?.receipt_url) {
                                      window.open(resolveReceiptOpenUrl(res.data.receipt_url), '_blank');
                                    } else {
                                      toast.error(res.data?.message || 'Receipt not available.');
                                    }
                                  } catch (err) {
                                    toast.error(err.response?.data?.message || 'Failed to load receipt.');
                                  } finally {
                                    setDownloadingReceiptId(null);
                                  }
                                }}
                              >
                                {downloadingReceiptId === payment.parent_subscription_installment_id ? '…' : 'Receipt'}
                              </Button>
                            </Tooltip>
                          ) : sub.cashier_subscription_id ? (
                            <Tooltip title="Load Stripe invoice (opens in new tab)" arrow>
                              <Button
                                size="small"
                                startIcon={
                                  loadingCashierSubscriptionReceiptId === sub.cashier_subscription_id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <DownloadIcon />
                                  )
                                }
                                variant="outlined"
                                disabled={loadingCashierSubscriptionReceiptId === sub.cashier_subscription_id}
                                sx={{
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                  bgcolor: 'rgba(22, 163, 74, 0.05)',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    bgcolor: 'rgba(22, 163, 74, 0.1)',
                                    borderColor: '#16a34a',
                                  },
                                }}
                                onClick={async () => {
                                  const sid = sub.cashier_subscription_id;
                                  setLoadingCashierSubscriptionReceiptId(sid);
                                  try {
                                    const path =
                                      billingParentId != null
                                        ? `admin/parent/${billingParentId}/subscription-invoice-receipt`
                                        : 'parent/billing/subscription-invoice-receipt';
                                    const res = await api.get(path, {
                                      params: { cashier_subscription_id: sid },
                                    });
                                    const url =
                                      res.data?.receipt_url ||
                                      res.data?.hosted_invoice_url ||
                                      res.data?.receipt_pdf_url;
                                    if (res.data?.success && url) {
                                      openReceiptInBrowser(url);
                                    } else {
                                      toast.error(res.data?.message || 'Receipt not available.');
                                    }
                                  } catch (err) {
                                    toast.error(err.response?.data?.message || 'Failed to load receipt.');
                                  } finally {
                                    setLoadingCashierSubscriptionReceiptId(null);
                                  }
                                }}
                              >
                                {loadingCashierSubscriptionReceiptId === sub.cashier_subscription_id ? '…' : 'Receipt'}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>—</Typography>
                          )
                        ) : (
                          // Payment is PENDING - Receipt column: Pay Invoice link if available, else —
                          payment.receipt_url ? (
                            <Tooltip title="Pay Invoice" arrow>
                              <Button
                                size="small"
                                startIcon={<PaymentIcon />}
                                variant="contained"
                                sx={{
                                  bgcolor: '#dc2626',
                                  color: '#fff',
                                  fontSize: '11px',
                                  py: 0.5,
                                  px: 1.5,
                                  minWidth: 'auto',
                                  borderRadius: '6px',
                                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.2)',
                                  '&:hover': {
                                    bgcolor: '#b91c1c',
                                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                                  }
                                }}
                                onClick={() => window.open(resolveReceiptOpenUrl(payment.receipt_url), '_blank')}
                              >
                                Pay Invoice
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>—</Typography>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ); })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}
      {subscriptionCount > 0 && (
        <Card
          elevation={0}
          sx={{
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <BillingPagination
            count={subscriptionCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}
    </Box>
  );
};

export default SubscriptionList;
